import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { PageHeader } from '@/components/PageHeader';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Plus, Pencil, Trash2, Search, Users } from 'lucide-react';
import { toast } from 'sonner';
import { EmptyState } from '@/components/EmptyState';

const empty = { name: '', rif: '', email: '', phone: '', address: '', contact_person: '' };

export default function ClientsPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try { setItems((await api.get('/clients')).data || []); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditingId(null); setForm(empty); setOpen(true); };
  const openEdit = (c) => { setEditingId(c.id); setForm({ ...empty, ...c }); setOpen(true); };

  const save = async (e) => {
    e?.preventDefault?.();
    if (!form.name.trim()) { toast.error('El nombre es requerido'); return; }
    setSaving(true);
    try {
      if (editingId) {
        await api.put(`/clients/${editingId}`, form);
        toast.success('Cliente actualizado');
      } else {
        await api.post('/clients', form);
        toast.success('Cliente creado');
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'No se pudo guardar');
    } finally { setSaving(false); }
  };

  const remove = async (c) => {
    if (!window.confirm(`¿Eliminar a "${c.name}"?`)) return;
    try { await api.delete(`/clients/${c.id}`); toast.success('Cliente eliminado'); load(); }
    catch { toast.error('No se pudo eliminar'); }
  };

  const filtered = items.filter((i) =>
    [i.name, i.rif, i.email, i.contact_person].some((x) => (x || '').toLowerCase().includes(query.toLowerCase()))
  );

  return (
    <div>
      <PageHeader
        title="Clientes"
        subtitle="Tu base de clientes para reutilizar en cotizaciones."
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="btn-primary h-10" onClick={openCreate} data-testid="clients-new-button">
                <Plus className="mr-1.5 h-4 w-4" /> Nuevo cliente
              </Button>
            </DialogTrigger>
            <DialogContent className="glass border-[var(--glass-border)] bg-[#0F1626]">
              <DialogHeader>
                <DialogTitle>{editingId ? 'Editar cliente' : 'Nuevo cliente'}</DialogTitle>
              </DialogHeader>
              <form className="grid grid-cols-2 gap-3" onSubmit={save}>
                <div className="col-span-2 space-y-1.5">
                  <Label htmlFor="name">Nombre / Razón social</Label>
                  <Input id="name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="clients-form-name" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="rif">RIF / ID</Label>
                  <Input id="rif" value={form.rif} onChange={(e) => setForm({ ...form, rif: e.target.value })} data-testid="clients-form-rif" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="contact_person">Persona de contacto</Label>
                  <Input id="contact_person" value={form.contact_person} onChange={(e) => setForm({ ...form, contact_person: e.target.value })} data-testid="clients-form-contact" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="email">Email</Label>
                  <Input id="email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="clients-form-email" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="phone">Teléfono</Label>
                  <Input id="phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="clients-form-phone" />
                </div>
                <div className="col-span-2 space-y-1.5">
                  <Label htmlFor="address">Dirección</Label>
                  <Textarea id="address" rows={2} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="clients-form-address" />
                </div>
                <DialogFooter className="col-span-2">
                  <Button type="button" variant="ghost" onClick={() => setOpen(false)}>Cancelar</Button>
                  <Button type="submit" className="btn-primary" disabled={saving} data-testid="clients-form-save">{saving ? 'Guardando…' : 'Guardar'}</Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        }
      />

      <div className="glass overflow-hidden rounded-xl">
        <div className="flex items-center gap-3 border-b border-[var(--glass-border)] p-3">
          <Search className="h-4 w-4 text-muted-foreground" />
          <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Buscar por nombre, RIF, email…" className="h-9 border-0 bg-transparent focus-visible:ring-0" data-testid="clients-search-input" />
          <span className="font-mono text-xs text-muted-foreground">{filtered.length} clientes</span>
        </div>
        {loading ? (
          <div className="p-8 text-center text-sm text-muted-foreground">Cargando…</div>
        ) : filtered.length === 0 ? (
          <EmptyState
            icon={Users}
            title={query ? 'Sin resultados' : 'Sin clientes'}
            description={query ? 'Intenta otra búsqueda.' : 'Agrega tu primer cliente para empezar.'}
            action={!query && <Button className="btn-primary" onClick={openCreate}><Plus className="mr-1.5 h-4 w-4" /> Crear cliente</Button>}
          />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Cliente</TableHead>
                <TableHead>RIF / ID</TableHead>
                <TableHead>Contacto</TableHead>
                <TableHead>Email</TableHead>
                <TableHead className="w-[100px] text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((c) => (
                <TableRow key={c.id} className="hover:bg-[var(--surface-1)]">
                  <TableCell>
                    <div className="font-medium">{c.name}</div>
                    {c.address && <div className="max-w-md truncate text-xs text-muted-foreground">{c.address}</div>}
                  </TableCell>
                  <TableCell><span className="font-mono text-sm">{c.rif || '—'}</span></TableCell>
                  <TableCell><span className="text-sm">{c.contact_person || '—'}</span></TableCell>
                  <TableCell><span className="text-sm text-muted-foreground">{c.email || '—'}</span></TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="icon" onClick={() => openEdit(c)} data-testid={`clients-edit-${c.name}`}><Pencil className="h-4 w-4" /></Button>
                    <Button variant="ghost" size="icon" onClick={() => remove(c)} data-testid={`clients-delete-${c.name}`} className="text-[#FF8FA3] hover:text-[#FF5C7A]"><Trash2 className="h-4 w-4" /></Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}
